import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * LevelUpFooter - Simplified footer design
 */
function LevelUpFooter() {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  // Footer link sections
  const footerSections = [
    {
      title: 'Resources',
      links: [
        { label: 'Home', href: '/browse' },
        { 
          label: 'About "Why Microsoft"', 
          href: '/about', 
          normalPart: 'About ',
          boldPart: '"Why Microsoft"'
        },
        { label: 'LevelUp Courses', href: 'https://skillupwithlevelup.com/courses', external: true },
        { label: 'Microsoft Partner Network', href: 'https://partner.microsoft.com', external: true },
      ]
    },
    {
      title: 'Support',
      links: [
        { label: 'Contact Us', href: 'https://skillupwithlevelup.com/contact-us', external: true },
      ]
    },
    {
      title: 'Legal',
      links: [
        { label: 'Trust Center', href: 'https://technofocus.ai/trust', external: true },
        { label: 'Privacy Policy', href: 'https://technofocus.ai/privacy', external: true },
        { 
          label: '"Why Microsoft" Terms of Use', 
          href: '/terms-of-use', 
          boldPart: '"Why Microsoft" ',
          normalPart: 'Terms of Use',
          boldFirst: true
        },
        { label: 'LevelUp Terms of Use', href: 'https://skillupwithlevelup.com/termsofuse', external: true },
      ]
    }
  ];

  return (
    <footer 
      style={{ 
        backgroundColor: '#1B5E5F',
        color: '#FFFFFF'
      }}
      role="contentinfo"
      aria-label="Site footer"
    >
      {/* Main Footer Content */}
      <div style={{ 
        maxWidth: '1400px', 
        margin: '0 auto', 
        padding: '60px 24px 40px'
      }}>
        <div style={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '48px'
        }}>
          {/* Logo & Description Section - Only LevelUp logo */}
          <div style={{ gridColumn: 'span 1' }}>
            {/* LevelUp Logo only */}
            <div style={{ marginBottom: '24px' }}>
              <a 
                href="https://skillupwithlevelup.com" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ display: 'inline-block' }}
                aria-label="Visit LevelUp website (opens in new tab)"
              >
                <img 
                  src="/levelup-logo-light.svg" 
                  alt="LevelUp" 
                  style={{ height: '28px', width: 'auto' }}
                />
              </a>
            </div>
            <p style={{ 
              fontSize: '14px', 
              color: 'rgba(255,255,255,0.8)',
              lineHeight: '1.6',
              marginBottom: '0',
              maxWidth: '280px'
            }}>
              <strong style={{ fontWeight: '700', display: 'block', marginBottom: '4px' }}>Why Microsoft – Compete Enablement for Partners</strong>
              {t('common.tagline')}
            </p>
          </div>

          {/* Link Sections */}
          {footerSections.map((section, index) => (
            <nav key={index} aria-labelledby={`footer-nav-${index}`}>
              <h4 
                id={`footer-nav-${index}`}
                style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#FFFFFF',
                  marginBottom: '20px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                {section.title}
              </h4>
              <ul style={{ 
                listStyle: 'none', 
                padding: 0, 
                margin: 0 
              }}>
                {section.links.map((link, linkIndex) => (
                  <li key={linkIndex} style={{ marginBottom: '12px' }}>
                    <a
                      href={link.href}
                      target={link.external ? '_blank' : '_self'}
                      rel={link.external ? 'noopener noreferrer' : undefined}
                      aria-label={link.external ? `${link.label} (opens in new tab)` : link.label}
                      style={{
                        color: 'rgba(255,255,255,0.8)',
                        textDecoration: 'none',
                        fontSize: '14px',
                        fontWeight: link.bold ? '700' : 'normal',
                        transition: 'color 0.2s',
                        outline: 'none'
                      }}
                      onFocus={(e) => {
                        e.target.style.color = '#C4E538';
                        e.target.style.textDecoration = 'underline';
                      }}
                      onBlur={(e) => {
                        e.target.style.color = 'rgba(255,255,255,0.8)';
                        e.target.style.textDecoration = 'none';
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#C4E538'}
                      onMouseLeave={(e) => e.target.style.color = 'rgba(255,255,255,0.8)'}
                    >
                      {link.normalPart && link.boldPart ? (
                        link.boldFirst ? (
                          <>
                            <strong style={{ fontWeight: '700' }}>{link.boldPart}</strong>
                            <span style={{ fontWeight: 'normal' }}>{link.normalPart}</span>
                          </>
                        ) : (
                          <>
                            <span style={{ fontWeight: 'normal' }}>{link.normalPart}</span>
                            <strong style={{ fontWeight: '700' }}>{link.boldPart}</strong>
                          </>
                        )
                      ) : (
                        link.label
                      )}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          ))}

          {/* Sponsored By Section - Only Microsoft logo */}
          <div>
            <h4 style={{ 
              fontSize: '14px', 
              fontWeight: '600', 
              color: '#FFFFFF',
              marginBottom: '20px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Sponsored By
            </h4>
            <img 
              src="/ms-levelup-logo.svg" 
              alt="Sponsored by Microsoft" 
              style={{ height: '32px', width: 'auto' }}
            />
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div style={{ 
        borderTop: '1px solid rgba(255,255,255,0.1)',
        backgroundColor: '#164849'
      }}>
        <div style={{ 
          maxWidth: '1400px', 
          margin: '0 auto', 
          padding: '20px 24px',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '16px'
        }}>
          <p style={{ 
            fontSize: '13px', 
            color: 'rgba(255,255,255,0.7)',
            margin: 0
          }}>
            {t('footer.copyright', { year: currentYear })}
          </p>
          <nav aria-label="Legal links" style={{ 
            display: 'flex', 
            gap: '24px',
            flexWrap: 'wrap'
          }}>
            <a
              href="https://technofocus.ai/privacy"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Privacy policy (opens in new tab)"
              style={{
                color: 'rgba(255,255,255,0.7)',
                textDecoration: 'none',
                fontSize: '13px',
                outline: 'none'
              }}
              onFocus={(e) => {
                e.target.style.color = '#C4E538';
                e.target.style.textDecoration = 'underline';
              }}
              onBlur={(e) => {
                e.target.style.color = 'rgba(255,255,255,0.7)';
                e.target.style.textDecoration = 'none';
              }}
              onMouseEnter={(e) => e.target.style.color = '#C4E538'}
              onMouseLeave={(e) => e.target.style.color = 'rgba(255,255,255,0.7)'}
            >
              {t('footer.privacy')}
            </a>
            <a
              href="/terms-of-use"
              aria-label="Terms of use"
              style={{
                color: 'rgba(255,255,255,0.7)',
                textDecoration: 'none',
                fontSize: '13px',
                outline: 'none'
              }}
              onFocus={(e) => {
                e.target.style.color = '#C4E538';
                e.target.style.textDecoration = 'underline';
              }}
              onBlur={(e) => {
                e.target.style.color = 'rgba(255,255,255,0.7)';
                e.target.style.textDecoration = 'none';
              }}
              onMouseEnter={(e) => e.target.style.color = '#C4E538'}
              onMouseLeave={(e) => e.target.style.color = 'rgba(255,255,255,0.7)'}
            >
              {t('footer.termsOfUse')}
            </a>
          </nav>
        </div>
      </div>
    </footer>
  );
}

export default LevelUpFooter;
